// components/EmployeeCalendar/EmployeeCalendar.tsx
import { useState, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { classNames } from "primereact/utils";
import styles from "./EmployeeCalendar.module.scss";
import { RenderWhen } from "../../../../infrastructure/components/RenderWhen";
import { Icon, IconType } from "../../../../icons/Icon";
import { useAppContext } from "../../../../../AppContext";
import { IEmployeeWithAvailabilityModel } from "./models/employeeCalendarModels";
import { AppDialog } from "../../../AppDialog/AppDialog";
import { EmployeeCalendarDialog } from "./components/EmployeeCalendarDialog";
import { AppTooltip } from "../../../tooltip/AppTooltip";

import { dvSize } from "../../../../infrastructure/utilities/viewportUnitsUtility";
interface IEmployeeCalendarProps {
  className?: string;
  employeeFieldName: string;
  dateFieldName: string;
  durationFieldName: string;
  /** The selected service's buffer after the reservation; counted as busy time too. */
  bufferAfterMinutes?: number;
  employeesData?: IEmployeeWithAvailabilityModel[];
  label?: string;
  required?: boolean;
  width?: string;
  disabled?: boolean;
  placeholder?: string;
  tooltip?: string;
}

export const EmployeeCalendar = ({
  className,
  employeeFieldName,
  dateFieldName,
  durationFieldName,
  bufferAfterMinutes,
  employeesData = [],
  label,
  required = false,
  width,
  disabled,
  placeholder = "Изберете служител и дата",
  tooltip,
}: IEmployeeCalendarProps) => {
  const { watch, formState } = useFormContext();
  const { isDesktopOrTablet } = useAppContext();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const selectedDate = watch(dateFieldName) as Date | undefined;
  const selectedEmployeeId = watch(employeeFieldName) as number | undefined;
  const selectedDuration = watch(durationFieldName) as number | undefined;

  if (!width) {
    if (isDesktopOrTablet) {
      width = "600px";
    } else {
      width = dvSize("80dvw");
    }
  }

  const displayText = useMemo(() => {
    if (!selectedDate && !selectedEmployeeId) {
      return placeholder;
    }

    if (!selectedDate) {
      const employee = employeesData.find((i) => i.id === selectedEmployeeId);
      return employee ? `${employee.name} - Изберете дата` : "Изберете дата";
    }

    if (!selectedEmployeeId) {
      const dateStr = `${String(selectedDate.getDate()).padStart(
        2,
        "0"
      )}/${String(selectedDate.getMonth() + 1).padStart(
        2,
        "0"
      )}/${selectedDate.getFullYear()}`;
      const timeStr = `${String(selectedDate.getHours()).padStart(
        2,
        "0"
      )}:${String(selectedDate.getMinutes()).padStart(2, "0")}`;
      return `${dateStr} ${timeStr} - Изберете служител`;
    }

    const employee = employeesData.find((i) => i.id === selectedEmployeeId);
    const dateStr = `${String(selectedDate.getDate()).padStart(
      2,
      "0"
    )}/${String(selectedDate.getMonth() + 1).padStart(
      2,
      "0"
    )}/${selectedDate.getFullYear()}`;
    const timeStr = `${String(selectedDate.getHours()).padStart(
      2,
      "0"
    )}:${String(selectedDate.getMinutes()).padStart(2, "0")}`;

    return `${employee?.name} - ${dateStr} ${timeStr}`;
  }, [selectedDate, selectedEmployeeId, employeesData, placeholder]);

  const hasError =
    formState.errors[employeeFieldName] || formState.errors[dateFieldName];
  const isTouched =
    formState.touchedFields[employeeFieldName] ||
    formState.touchedFields[dateFieldName];

  return (
    <>
      <div
        style={{ width: width }}
        className={classNames(className, styles.root, {
          [styles.root_disabled]: disabled,
        })}
      >
        <RenderWhen condition={!!label}>
          <div
            className={classNames(styles.root_label, {
              [styles.root_label_interacted_with]:
                !!selectedDate || !!selectedEmployeeId || isTouched,
              [styles.root_label_error]: !!hasError,
            })}
          >
            {label}
            {required ? "*" : ""}
            <AppTooltip content={tooltip} />
          </div>
        </RenderWhen>

        <div className={styles.root_input_container}>
          <div
            className={classNames(styles.root_input, {
              [styles.root_input_error]: !!hasError,
            })}
            onClick={() => !disabled && setIsDialogOpen(true)}
          >
            <span className={styles.root_input_text}>{displayText}</span>
            <div className={styles.root_input_icon_container}>
              <Icon type={IconType.Calendar} className={styles.root_input_icon} color="currentColor" />
            </div>
          </div>
        </div>
      </div>

      <AppDialog
        visible={isDialogOpen}
        onHide={() => setIsDialogOpen(false)}
        style={{ width: "min(960px, 95vw)", maxWidth: "95vw", maxHeight: dvSize("90dvh") }}
        showHeader={false}
        className={styles.dialog}
      >
        <EmployeeCalendarDialog
          employeeFieldName={employeeFieldName}
          dateFieldName={dateFieldName}
          durationFieldName={durationFieldName}
          employeesData={employeesData}
          selectedEmployeeId={selectedEmployeeId}
          selectedDate={selectedDate}
          selectedDuration={selectedDuration}
          bufferAfterMinutes={bufferAfterMinutes}
          onClose={() => setIsDialogOpen(false)}
        />
      </AppDialog>
    </>
  );
};